import type {MakeRequest, RestResponse} from './request'
import {dataResponseConverter} from './request'
import type {AppStorage, LocationModel, PlaceInfoModel, Unknown} from '../index'
import {timestamp} from '../utils/request.utils'
import type {GeoApi} from './geoApi'
import type {GoogleApi} from './google.api'

export enum StartAppStatus {
  ok,
  canUpdate,
  mustUpdate,
  tracking,
  loading,
  location,
  askLocation,
  pushToken,
  checkVersion,
  error,
}

export interface CheckStatus {
  status: StartAppStatus
  categoryVersion: number
}

export interface CheckVersionResponse {
  readonly appVersionMin: number
  readonly appVersionRecommend: number
  readonly categoriesVersion: number
}

type FetchAndSaveLocationProblem = Record<string, Error | null>
export interface FetchAndSaveLocationResult {
  problems: FetchAndSaveLocationProblem
  result: boolean
}

export class StartApp {
  constructor(
    private request: MakeRequest,
    private storage: AppStorage,
    private geoApi: GeoApi,
    private googleApi: GoogleApi,
    private requestLocationPermissions?: () => Promise<boolean>,
    private getIosLocation?: () => Promise<LocationModel | null>,
  ) {}

  /** @deprecated */
  requestAppConfiguration = async (
    currentAppVersion: string,
    platformOS: 'ios' | 'android' | string,
  ): Promise<CheckStatus> => {
    const appVersion = currentAppVersion

    const response = await this.request.newRequest<{
      appVersion: string
      appVersionAndroid: number
      appVersionIos: number
      wordsVersion: number
      obligeUpdateAndroid: number
      obligeUpdateIos: number
      categoriesVersion: number
    }>(
      {
        endpoint: 'v2/version',
        data: {
          appId: this.storage.platform,
          timestamp: timestamp(),
        },
      },
      dataResponseConverter,
    )

    const result = response.result
    let status: StartAppStatus = StartAppStatus.ok
    if (!result) return {status: status, categoryVersion: -1}

    const serverAppVersion = result.appVersion
    if (serverAppVersion && appVersion !== serverAppVersion) {
      const intent =
        platformOS === 'ios'
          ? result.obligeUpdateIos
          : result.obligeUpdateAndroid
      status = intent ? StartAppStatus.mustUpdate : StartAppStatus.canUpdate
    }
    return {status, categoryVersion: result.categoriesVersion}
  }

  checkAppVersion = (): Promise<RestResponse<CheckVersionResponse>> => {
    const timestamp = Math.floor(Date.now() / 1000)
    return this.request.newRequest<CheckVersionResponse>(
      {
        endpoint: 'v3/version',
        body: {
          data: {
            appId: this.storage.platform,
            timestamp,
          },
        },
      },
      dataResponseConverter,
    )
  }

  fetchAndSaveLocationAndAddress = async (): Promise<FetchAndSaveLocationResult> => {
    const problems: FetchAndSaveLocationProblem = {}
    let location: LocationModel | Unknown = null
    const isGranted = await this.requestLocationPermissions?.()
    // access location from mobile if permissions was grant
    try {
      if (isGranted) location = await this.getIosLocation?.()
    } catch (e) {
      problems['request-getIosLocation'] = e as Error
    }
    // if there is no location fetch location by ip from server
    try {
      if (!location)
        location = (await this.geoApi.fetchGPSByIP())?.result ?? null
    } catch (e) {
      problems['request-fetchGPSByIP'] = e as Error
    }
    if (!location) {
      problems['invalid-state-no-location'] = null
      return {result: false, problems}
    }
    // save new location
    await this.storage.setUserLocation(location)
    // get last saved filter location
    const filterLocation = this.storage.location
    if (filterLocation?.latitude === 0 && filterLocation?.longitude === 0) {
      problems['zeroCoords'] = null
      return {result: true, problems}
    }
    let placeInfo: PlaceInfoModel | null
    try {
      placeInfo = await this.googleApi.fetchGeocode(filterLocation)
    } catch (e) {
      problems['request fetchGeocode'] = e as Error
      return {result: false, problems}
    }
    if (placeInfo) await this.storage.saveAddressText(placeInfo.address)
    return {result: true, problems}
  }
}
